/**
 *  Copyright (c) 2015, The Regents of the University of California,
 *  through Lawrence Berkeley National Laboratory (subject to receipt
 *  of any required approvals from the U.S. Dept. of Energy).
 *  All rights reserved.
 *
 *  This source code is licensed under the BSD-style license found in the
 *  LICENSE file in the root directory of this source tree.
 */

/* eslint max-len:0 */

import React from "react";
import _ from "underscore";
import { format } from "d3-format";
import moment from "moment";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import NativeListener from "react-native-listener";
import {
  Charts,
  ChartContainer,
  ChartRow,
  YAxis,
  BarChart,
  Resizable,
  styler,
} from "react-timeseries-charts";

import {
  TimeSeries,
  Index,
} from "pondjs";

import CrossHairs from "../../lib/CrossHairs.jsx";

import AuthService from "../../../providers/auth/AuthService.js";
import GlasschainHttpClient from "../../../providers/http/GlasschainMsaHttpClient.js";
import { Card } from "react-bootstrap";

function buildPoints(data) {
  let pointData = [];
  for (let i = 0; i < data.length; i++) {
    // clean up the data
    const dt = data[i].postDateTime.replace(" ", "0");
    pointData.push([dt, data[i].ok, data[i].reject]);
  }
  return pointData;
}

function adjustedMaxCount(data) {
  let maxY = 0;
  for (let i = 0; i < data.length; i++) {
    var testMax = Math.max(maxY, data[i].ok + data[i].reject);
    if (testMax > maxY) maxY = testMax;
  }
  return maxY;
}

// init
const formatter = format("");
var priorDate = new Date();
priorDate.setDate(priorDate.getDate() - 30);
var nextDate = new Date();
nextDate.setDate(nextDate.getDate() + 1);

const emptyData = [
  { postDateTime: priorDate.toISOString(), millSecTime: 0, ok: 50, reject: 10 },
  {
    postDateTime: new Date(Date.now()).toISOString(),
    millSecTime: 0,
    ok: 30,
    reject: 10,
  },
  { postDateTime: nextDate.toISOString(), millSecTime: 0, ok: 0, reject: 0 }, // add a day beyond
];

const dta = buildPoints(emptyData);
const series = new TimeSeries({
  name: "Receipts",
  columns: ["index", "ok", "reject"],
  points: dta.map(([d, value1, value2]) => [
    Index.getIndexString("1h", new Date(d)),
    value1,
    value2,
  ]),
});
const maxY = adjustedMaxCount(emptyData);

const activityDaysAgo = 15

export default class ActivityBarChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tracker: null,
      timerange: series.range(),
      x: null,
      y: null,
      dataSeries: series,
      maxy: maxY,
      selection: null,
      isLoading: true
    };
    this.authClient = new AuthService();
    this.apiClient = new GlasschainHttpClient();

    this.cancelWheelBubble = this.cancelWheelBubble.bind(this);
  }

  async componentDidMount() {
    var dataResult = await this.apiClient.fetchBatchStatusByHour(activityDaysAgo);
    //currencySeries.points = buildPoints(dataResult.data);
    let newDataSeries = new TimeSeries({
      name: "QA Observations",
      columns: ["index", "ok", "reject"],
      points: buildPoints(dataResult.data).map(([d, value1, value2]) => [
        Index.getIndexString("1h", new Date(d)),
        value1,
        value2,
      ]),
    });
    this.setState({
      dataSeries: newDataSeries,
    });
    let x = newDataSeries.range();
    this.setState({ timerange: x });
    let maxY = adjustedMaxCount(dataResult.data);
    this.setState({ maxy: maxY });
    this.setState({isLoading: false});
  }

  componentWillUnmount() {
    //clearInterval(this.interval);
  }

  handleTimeRangeChange = (timerange) => {
    this.setState({ timerange });
  };

  handleMouseMove = (x, y) => {
    this.setState({ x, y });
  };

  handleTrackerChanged = (tracker) => {
    if (!tracker) {
      this.setState({ tracker, x: null, y: null });
    } else {
      this.setState({ tracker });
    }
  };

  cancelWheelBubble = (ev) => {
    ev.preventDefault();
  };

  render() {

    const loadingIcon = (this.state.isLoading) ? 
    <FontAwesomeIcon icon="sync-alt" color="green" spin/> :
    "" 

    const style = styler([
      { key: "ok", color: "#539268", selected: "#168d3e" },
      { key: "reject", color: "#e60c05", selected: "#ec8a87" },
    ]);

    const highlight = this.state.highlight;
    let infoValues = [];
    if (highlight) {
      const trafficText = `${formatter(highlight.event.get(highlight.column))}`;
      infoValues = [{ label: "Observations", value: trafficText }];
    }

    return (
      <Card>
        <Card.Header>
          <span style={{float: "left"}}>Enterprise {activityDaysAgo}-Day Activity</span>
          <span style={{float: "right"}}>{loadingIcon}</span>
        </Card.Header>

        <Card.Body>
      <NativeListener
        onWheel={this.cancelWheelBubble}
        onScroll={this.cancelWheelBubble}
      >
        <Resizable>
          <ChartContainer
            timeRange={this.state.timerange}
            onBackgroundClick={() => this.setState({ selection: null })}
            maxTime={this.state.dataSeries.range().end()}
            minTime={this.state.dataSeries.range().begin()}
            timeAxisAngledLabels={true}
            timeAxisHeight={65}
            enablePanZoom={true}
            onMouseMove={(x, y) => this.handleMouseMove(x, y)}
            onTimeRangeChanged={this.handleTimeRangeChange.bind(this)}
            minDuration={1000 * 60 * 60} // * 60 * 24 * 30}
          >
            <ChartRow height="120">
              <YAxis
                id="posts"
                label="QA Observations"
                min={0}
                max={this.state.maxy}
                format=""
                width="70"
                type="linear"
              />
              <Charts>
                <BarChart
                  axis="posts"
                  style={style}
                  spacing={1}
                  columns={["ok", "reject"]}
                  series={this.state.dataSeries}
                  minBarHeight={1}
                  info={infoValues}
                  highlighted={this.state.highlight}
                  onHighlightChange={(highlight) =>
                    this.setState({ highlight })
                  }
                  selected={this.state.selection}
                  onSelectionChange={(selection) =>
                    this.setState({ selection })
                  }
                />
                <CrossHairs x={this.state.x} y={this.state.y} />
              </Charts>
            </ChartRow>
          </ChartContainer>
        </Resizable>
      </NativeListener>
      </Card.Body>
      </Card>
    );
  }
}
